import CopyButton from "@components/buttons/CopyButton";
import CustomButton from "@components/buttons/CustomButton";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import SlidingPanel from "@components/sliders/SlidingPanel";
import { primeServices } from "@services/apis/Prime";
import {
  CreateBrlTradeRequest,
  CreateBrlTradeResponse,
} from "@services/models/Prime/CreateBrlTradeModel";
import { useState } from "react";
import useSnackbarMessage from "../../../utils/HandleSuccessErrorSnackbars";

interface CreateTradeSliderProps {
  isOpen: boolean;
  onClose: () => void;
}

interface TradeFormData {
  amount: string;
  callBackUrl: string;
  redirectUrl: string;
  userEmail: string;
}

const CreateTradeSlider = ({ isOpen, onClose }: CreateTradeSliderProps) => {
  const [formData, setFormData] = useState<TradeFormData>({
    amount: "",
    callBackUrl: "",
    redirectUrl: "",
    userEmail: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [tradeResponse, setTradeResponse] =
    useState<CreateBrlTradeResponse | null>(null);
  const [merchantOrderId, setMerchantOrderId] = useState<string>("");
  const { handleSuccess } = useSnackbarMessage();
  const [errors, setErrors] = useState<Partial<TradeFormData>>({});

  const generateOrderId = (): string => {
    return `insights-${crypto.randomUUID()}`;
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<TradeFormData> = {};

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = "Amount is required and must be greater than 0";
    }

    // Only validate email format if it's provided
    if (formData.userEmail && !/\S+@\S+\.\S+/.test(formData.userEmail)) {
      newErrors.userEmail = "Please enter a valid email address";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange =
    (field: keyof TradeFormData) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: e.target.value,
      }));

      // Clear error when user starts typing
      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: undefined,
        }));
      }
    };

  const handleCreateAnother = () => {
    setIsSuccess(false);
    setTradeResponse(null);
    setMerchantOrderId("");
    setFormData({
      amount: "",
      callBackUrl: "",
      redirectUrl: "",
      userEmail: "",
    });
    setErrors({});
  };

  const handleClose = () => {
    setIsSuccess(false);
    setTradeResponse(null);
    setMerchantOrderId("");
    setFormData({
      amount: "",
      callBackUrl: "",
      redirectUrl: "",
      userEmail: "",
    });
    setErrors({});
    onClose();
  };

  return (
    <SlidingPanel
      isOpen={isOpen}
      showConfirm={false}
      heading={isSuccess ? "Trade Created Successfully" : "Create Trade"}
      onClose={handleClose}
    >
      <div className="flex flex-col px-[16px] pb-[16px] pt-[8px]">
        {isSuccess && tradeResponse ? (
          // Success State
          <div className="space-y-4">
            <div className="bg-green-50 border border-border-subtle rounded-lg p-4">
              <p className="text-green-800 font-semibold text-sm mb-2">
                Trade Details
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="font-medium text-text-base text-[12px]">
                      Instance ID:
                    </span>
                    <span className="ml-2 font-mono bg-gray-100 px-2 py-1 rounded text-text-base text-[12px]">
                      {tradeResponse.data.instanceId}
                    </span>
                  </div>
                  <CopyButton
                    copyText={tradeResponse.data.instanceId}
                    copyMessage="Instance ID copied to clipboard!"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="font-medium text-text-base text-[12px]">
                      Trade ID:
                    </span>
                    <span className="ml-2 font-mono bg-gray-100 px-2 py-1 rounded text-text-base text-[12px]">
                      {tradeResponse.data.tradeId}
                    </span>
                  </div>
                  <CopyButton
                    copyText={tradeResponse.data.tradeId.toString()}
                    copyMessage="Trade ID copied to clipboard!"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="font-medium text-text-base text-[12px]">
                      Merchant Order ID:
                    </span>
                    <span className="ml-2 font-mono bg-gray-100 px-2 py-1 rounded text-text-base text-[12px]">
                      {merchantOrderId}
                    </span>
                  </div>
                  <CopyButton
                    copyText={merchantOrderId}
                    copyMessage="Merchant Order ID copied to clipboard!"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center flex-1">
                    <span className="font-medium text-text-base text-[12px]">
                      Trade Link:
                    </span>
                    <a
                      href={tradeResponse.data.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-2 font-mono bg-gray-100 px-2 py-1 rounded text-blue-600 hover:text-text-button-tertiary underline cursor-pointer break-all  text-[12px]"
                    >
                      {tradeResponse.data.url}
                    </a>
                  </div>
                  <CopyButton
                    copyText={tradeResponse.data.url}
                    copyMessage="Trade link copied to clipboard!"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <CustomButton
                text="Create Another"
                isActive={true}
                onClick={handleCreateAnother}
                className="flex-1"
                isHollow={true}
              />
              <CustomButton
                text="Close"
                isActive={true}
                onClick={handleClose}
                className="flex-1"
              />
            </div>
          </div>
        ) : (
          // Form State
          <div className="space-y-4">
            <div>
              <GlobalTextInputField
                value={formData.amount}
                onChange={handleInputChange("amount")}
                placeholder="Enter amount"
                title="Amount *"
                type="number"
                allowDecimal={true}
                className="w-full"
              />
              {errors.amount && (
                <p className="text-text-status-red text-xs mt-1">
                  {errors.amount}
                </p>
              )}
            </div>

            <div>
              <GlobalTextInputField
                value={formData.callBackUrl}
                onChange={handleInputChange("callBackUrl")}
                placeholder="Enter callback URL"
                title="Callback URL"
                className="w-full"
              />
              {errors.callBackUrl && (
                <p className="text-text-status-red text-xs mt-1">
                  {errors.callBackUrl}
                </p>
              )}
            </div>

            <div>
              <GlobalTextInputField
                value={formData.redirectUrl}
                onChange={handleInputChange("redirectUrl")}
                placeholder="Enter redirect URL"
                title="Redirect URL"
                className="w-full"
              />
              {errors.redirectUrl && (
                <p className="text-text-status-red text-xs mt-1">
                  {errors.redirectUrl}
                </p>
              )}
            </div>

            <div>
              <GlobalTextInputField
                value={formData.userEmail}
                onChange={handleInputChange("userEmail")}
                placeholder="Enter user email"
                title="User Email"
                type="email"
                className="w-full"
              />
              {errors.userEmail && (
                <p className="text-text-status-red text-xs mt-1">
                  {errors.userEmail}
                </p>
              )}
            </div>

            <div className="flex gap-3 pt-4 w-full">
              <CustomButton
                text="CANCEL"
                isActive={true}
                onClick={handleClose}
                className="flex-1"
                isHollow={true}
              />
              <CustomButton
                text={"CREATE TRADE"}
                isActive={formData.amount !== ""}
                isButtonLoading={isSubmitting}
                onClick={async () => {
                  if (!validateForm()) {
                    return;
                  }

                  setIsSubmitting(true);

                  try {
                    const orderId = generateOrderId();
                    const tradeRequest: CreateBrlTradeRequest = {
                      isBuyTrade: 1,
                      merchantOrderId: orderId,
                      amount: parseFloat(formData.amount),
                      currencySymbol: "BRL",
                      callBackUrl: formData.callBackUrl,
                      redirectUrl: formData.redirectUrl,
                      userEmail: formData.userEmail,
                    };

                    const response =
                      await primeServices.createBrlTrade(tradeRequest);

                    console.log("Trade created successfully:", response);

                    // Store response data and show success state
                    setTradeResponse(response);
                    setMerchantOrderId(orderId);
                    setIsSuccess(true);

                    // Show success message
                    handleSuccess({ message: "Trade created successfully!" });
                  } catch (error) {
                    console.error("Error creating trade:", error);
                  } finally {
                    setIsSubmitting(false);
                  }
                }}
                className="flex-1"
              />
            </div>
          </div>
        )}
      </div>
    </SlidingPanel>
  );
};

export default CreateTradeSlider;
